// src/pages/SingleListingGenerator/utils.js
import { API_BASE_URL, MAX_BULLETS } from './constants';

/**
 * Prepares data for submission to the API
 * @param {Object} formData - Form data object
 * @param {Object} existingInput - Existing input data (for existing listing mode)
 * @param {Array} productImages - Array of product images
 * @param {Array} selectedTemplates - Array of selected template indices
 * @param {String} listingMode - 'new' or 'existing'
 * @param {Object} singleImage - Single image object
 * @param {Array} selectedTemplatesWithImages - Array of selected templates with images
 * @param {Object} dimensionData - Dimension data object
 * @param {Object} selectedAPlusTemplate - Selected A+ template object
 * @param {Array} aPlusSequence - Array of A+ template images
 * @param {String} originalId - Original ID for updates
 * @param {Array} uploadedImages - Array of uploaded images
 * @param {Array} bPlusSequence - Array of B+ template images (will be repeated to make 4)
 * @param {String} browsenode - Browse node value (from formData or asinProductData)
 * @returns {Object} Prepared payload and FormData object
 */
export const prepareSubmissionData = (
  formData,
  existingInput,
  productImages,
  selectedTemplates,
  listingMode,
  singleImage = null,
  selectedTemplatesWithImages = [],
  dimensionData = null,
  selectedAPlusTemplate = null,
  aPlusSequence = [],
  originalId = null,
  uploadedImages = [],
  bPlusSequence = [],
  browsenode = '',
  selectedFonts = null,
  listingUpgradePayload = null,
  variantsDraftData = null,
  zoomAreas = []
) => {
  const isExistingListing = listingMode === 'existing';

  // Create the product data object in the format expected by backend
  const productDataObj = {
    project_name: formData.projectName?.trim() || '',
    brand_name: formData.brandName?.trim() || '',
    category: formData.category?.trim() || '',
    sub_category: formData.subCategory?.trim() || '',
    productType: formData.productType?.trim() || '',
    browsenode: browsenode?.trim() || '',
    description: isExistingListing ? 
      (existingInput.description?.trim() || formData.briefDescription?.trim() || '') : 
      (formData.briefDescription?.trim() || ''),
    keywords: formData.keywords ? 
      formData.keywords.split(',').map(k => k.trim()).filter(k => k.length > 0) : [],
    bannedKeywords: formData.bannedKeywords ? 
      formData.bannedKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0) : [],
    model_number: formData.modelNumber?.trim() || '',
    sku_id: formData.skuId?.trim() || '',
    color: formData.color?.trim() || '',
    material: formData.material?.trim() || '',
    size: formData.size?.trim() || '',
    ean_number: formData.eanNumber?.trim() || '',
    key_attributes: [
      formData.feature1,
      formData.feature2,
      formData.feature3,
      formData.feature4,
      formData.feature5
    ].map(f => f?.trim()).filter(f => f && f.length > 0),
    selected_template_ids: selectedTemplates ? selectedTemplates.map((item, index) => {
      if (typeof item === 'string') {
        return item;
      }
      const templateIndex = typeof item === 'number' ? item : index;
      return `template${templateIndex + 1}`;
    }) : [],
    generate_infographics: (productImages.length > 0 && selectedTemplates.length > 0 && productImages.length === selectedTemplates.length) || 
                      (selectedTemplatesWithImages && selectedTemplatesWithImages.length > 0),
    is_existing: isExistingListing,
    template_first_selections: selectedTemplatesWithImages.map(template => {
      const applicationHeaders = {
        ...(template.applicationHeaders || {}),
        ...(formData.application1?.trim() && { header1: formData.application1.trim() }),
        ...(formData.application2?.trim() && { header2: formData.application2.trim() }),
        ...(formData.application3?.trim() && { header3: formData.application3.trim() })
      };
      
      return {
        template_id: template.id,
        template_name: template.name ?? template.id,
        ...(template.requiredImages != null && { required_images: template.requiredImages }),
        required_headers: 3,
        generate_images: true,
        application_headers: Object.keys(applicationHeaders).length > 0 ? applicationHeaders : null
      };
    }),

    has_dimensions: !!(dimensionData && dimensionData.isComplete),
    dimension_length: dimensionData?.length && dimensionData?.units 
      ? `${dimensionData.length} ${dimensionData.units}`.trim() 
      : (dimensionData?.length || ''),
    dimension_breadth: dimensionData?.breadth && dimensionData?.units 
      ? `${dimensionData.breadth} ${dimensionData.units}`.trim() 
      : (dimensionData?.breadth || ''),
    benefits: formData.benefits?.trim() || '',
    usage: formData.usage?.trim() || '',
    instructions: formData.instructions?.trim() || '',

    // Add A+ template data
    has_aplus_template: !!selectedAPlusTemplate,
    aplus_template_id: selectedAPlusTemplate?.id || '',
    aplus_template_name: selectedAPlusTemplate?.name || '',
    aplus_slide_count: selectedAPlusTemplate?.slides?.length || 0,
    has_aplus_images: !!(aPlusSequence && aPlusSequence.length > 0),
    aplus_image_count: aPlusSequence?.length || 0,
    aplus_theme: selectedAPlusTemplate?.id || '', 

    // Zoom areas from user selection [{x, y, w, h}, ...]
    zoom_areas: zoomAreas.map(({ x, y, w, h, label }) => ({ x, y, w, h, label: label || '' })),

    // Font data
    fonts: selectedFonts || {
      headerFont: { family: '', weight: '' },
      calloutTextFont: { family: '', weight: '' },
      bodyFont: { family: '', weight: '' }
    }
  };

  // Attach original id for updates (must be done after object creation)
  if (originalId) {
    productDataObj._id = originalId;
  }

  // Add existing listing specific fields
  if (isExistingListing) {
    if (existingInput.title?.trim()) {
      productDataObj.existing_title = existingInput.title.trim();
    }
    
    const validBullets = existingInput.bullets
      .map(b => b?.trim())
      .filter(b => b && b.length > 0);
    
    if (validBullets.length > 0) {
      productDataObj.existing_bullets = validBullets;
    }
    
    if (existingInput.description?.trim()) {
      productDataObj.existing_description = existingInput.description.trim();
    }
  }

  // Create FormData for API submission
  const formDataApi = new FormData();

  // --- Variants Tab: build child_variants and append images (if any) ---
  try {
    const variantsRaw = variantsDraftData?.variants;
    const hasVariantsPayload = variantsRaw && (Array.isArray(variantsRaw) ? variantsRaw.length > 0 : Object.keys(variantsRaw).length > 0);
    if (hasVariantsPayload) {
      const hasLevel3 = String(variantsDraftData.variantType || '').split('/').filter(Boolean).length >= 3;
      const childVariants = {};
      let childIndex = 1;
      // Store first image for each child to send as singleImageChild1, singleImageChild2, etc.
      const childFirstImages = {};
      const level1Values = Array.isArray(variantsDraftData.level1Values) ? variantsDraftData.level1Values : [];
      const variantsIsArray = Array.isArray(variantsRaw);
      const variantsByIndex = variantsIsArray ? variantsRaw : null;
      const variantsByValue = variantsIsArray ? null : (variantsRaw || {});
      const level1Keys = level1Values.length > 0 ? level1Values : (variantsIsArray ? [] : Object.keys(variantsByValue));
      const rowCount = variantsIsArray ? Math.max(level1Values.length, (variantsRaw || []).length) : level1Keys.length;

      for (let level1Idx = 0; level1Idx < rowCount; level1Idx++) {
        const level1Val = level1Values[level1Idx] ?? level1Keys[level1Idx] ?? '';
        const variantRows = variantsIsArray
          ? (Array.isArray(variantsByIndex[level1Idx]) ? variantsByIndex[level1Idx] : [])
          : (Array.isArray(variantsByValue[level1Val]) ? variantsByValue[level1Val] : []);

        if (!variantsIsArray && !variantsByValue[level1Val]) {
          continue;
        }

        variantRows.forEach((variantRow, variantRowIdx) => {
          const actualVariantRow = variantsIsArray ? (variantsByIndex[level1Idx] || [])[variantRowIdx] : variantsByValue[level1Val]?.[variantRowIdx];
          if (actualVariantRow !== variantRow) {
            return;
          }
          
          const level2Val = (variantRow?.level2 || '').trim();
          if (!level2Val) {
            return;
          }

          const processVariant = (level3Val, lvl3Index, imgKey, customSku = null, customVariantRow = null) => {
            if (hasLevel3 && !level3Val) return;
            
            const key = `child${childIndex++}`;
            const combinationParts = [];
            if (level1Val?.trim()) combinationParts.push(level1Val);
            combinationParts.push(level2Val);
            if (hasLevel3 && level3Val) combinationParts.push(level3Val);
            
            const combination = combinationParts.join(', ');
            // Use custom SKU if provided (from level3Field), otherwise use variant SKU
            const sku = customSku || (variantRow?.sku || '').trim() || '';
            
            childVariants[key] = {
              sku: sku,
              combination: combination,
              images: []
            };

            // Use customVariantRow if provided (for level3Fields), otherwise use variantRow
            const sourceRow = customVariantRow || variantRow;
            
            // CRITICAL: Ensure we're getting images ONLY from the current variantRow
            // For level3Fields: images are stored in level3Field.images
            // For legacy level3: images are stored in variantRow.images[imgKey]
            
            // Validate that sourceRow exists
            if (!sourceRow) {
              return;
            }
            
            // Get images: for level3Fields, use level3Field.images directly
            let nodeImages = [];
            if (customVariantRow && customVariantRow.images?.level3) {
              // level3Fields format: images are in customVariantRow.images.level3
              nodeImages = customVariantRow.images.level3 || [];
            } else if (sourceRow.images && imgKey) {
              // Legacy format: images are in sourceRow.images[imgKey]
              nodeImages = sourceRow.images[imgKey] || [];
            }
            
            // Validate images array
            if (!Array.isArray(nodeImages)) {
              return;
            }
            
            // Collect all valid images first
            const validImages = nodeImages.filter(imgObj => imgObj && imgObj?.file);
            
            if (validImages.length === 0) {
              return;
            }
            
            // Apply repetition logic to make 4 images (same as B+ images)
            const imagesToSend = [];
            const imageCount = validImages.length;
            
            if (imageCount === 1) {
              // 1 image -> [1,1,1,1]
              imagesToSend.push(validImages[0], validImages[0], validImages[0], validImages[0]);
            } else if (imageCount === 2) {
              // 2 images -> [1,2,1,2]
              imagesToSend.push(validImages[0], validImages[1], validImages[0], validImages[1]);
            } else if (imageCount === 3) {
              // 3 images -> [1,2,3,1]
              imagesToSend.push(validImages[0], validImages[1], validImages[2], validImages[0]);
            } else {
              // 4+ images -> use first 4
              imagesToSend.push(...validImages.slice(0, 4));
            }
            
            // Process and append images to FormData
            imagesToSend.forEach((imgObj, imgIdx) => {
              const filename = imgObj.file.name || `variant_${key}_${imgIdx + 1}.png`;
              const imageFormKey = `child_variants_${key}_images`;
              
              // Append to FormData - each child gets its own unique key
              formDataApi.append(imageFormKey, imgObj.file, filename);
              childVariants[key].images.push(filename);
            });
            
            // Store the first image for this child to send as singleImageChild1, singleImageChild2, etc.
            if (validImages.length > 0) {
              childFirstImages[key] = {
                file: validImages[0].file,
                filename: validImages[0].file.name || `variant_${key}_1.png`
              };
            }
          };

          if (hasLevel3) {
            // Handle level3Fields (new format with value, sku, images per field)
            const level3Fields = variantRow?.level3Fields;
            if (level3Fields && Array.isArray(level3Fields)) {
              // Process each level3Field as a separate combination
              level3Fields.forEach((level3Field, level3Index) => {
                const level3Val = (level3Field?.value || '').trim();
                if (level3Val) {
                  // Create a temporary variant structure for processVariant with level3Field's images
                  const tempVariant = {
                    ...variantRow,
                    images: {
                      level3: Array.isArray(level3Field.images) ? level3Field.images : []
                    }
                  };
                  // Process with level3Field's SKU if available, otherwise use variant SKU
                  const sku = (level3Field?.sku || '').trim() || (variantRow?.sku || '').trim();
                  processVariant(level3Val, level3Index, 'level3', sku, tempVariant);
                }
              });
              
              // If no valid level3Fields, still process as level2 variant
              if (level3Fields.length === 0 || level3Fields.every(f => !(f?.value || '').trim())) {
                processVariant(null, null, 'level2');
              }
            } else {
              // Legacy format: Handle level3 as array or string
              const level3Data = variantRow?.level3;
              const level3Array = Array.isArray(level3Data) ? level3Data : (level3Data ? [level3Data] : []);
              
              // Process each level3 value
              level3Array.forEach((level3Val, level3Index) => {
                const trimmedVal = (level3Val || '').trim();
                if (trimmedVal) {
                  const imgKey = 'level3';
                  processVariant(trimmedVal, level3Index, imgKey);
                }
              });
              
              // If no level3 values, still process as level2 variant
              if (level3Array.length === 0 || level3Array.every(v => !(v || '').trim())) {
                processVariant(null, null, 'level2');
              }
            }
          } else {
            processVariant(null, null, 'level2');
          }
        });
      }

      if (Object.keys(childVariants).length > 0) {
        formDataApi.append('child_variants', JSON.stringify(childVariants));
        
        // Append each child's first image as singleImageChild1, singleImageChild2, etc.
        Object.keys(childFirstImages).forEach(key => {
          const firstImage = childFirstImages[key];
          if (firstImage && firstImage.file) {
            // Extract child number from key (e.g., "child1" -> "1")
            const childNumber = key.replace('child', '');
            const singleImageKey = `singleImageChild${childNumber}`;
            formDataApi.append(singleImageKey, firstImage.file, firstImage.filename);
          }
        });
      }
    }
  } catch (e) {
    // Error processing variants; rethrow or handle as needed
  }

  // Backend expects 'product_data' (not 'product_data_json_str')
  formDataApi.append('product_data', JSON.stringify(productDataObj));

  // Optional: include listing upgrade payload from Listing Score so backend can use it in the LLM prompt
  if (listingUpgradePayload) {
    try {
      formDataApi.append('listing_upgrade_payload', JSON.stringify(listingUpgradePayload));
    } catch (e) {
      // Ignore serialization issues; generation can proceed without it
    }
  }

  formDataApi.append('generate_images', 'true');

  // Append A+ template data
  formDataApi.append('has_aplus_template', selectedAPlusTemplate ? 'true' : 'false');
  if (selectedAPlusTemplate) {
    formDataApi.append('aplus_template_data', JSON.stringify({
      id: selectedAPlusTemplate.id,
      name: selectedAPlusTemplate.name,
      theme: selectedAPlusTemplate.id, // This maps to theme1, theme2, etc.
      slide_count: selectedAPlusTemplate.slides.length
    }));
  }

  // Append images using the pattern your backend expects: 'images'
  if (productDataObj.generate_infographics && productImages.length > 0) {
    productImages.forEach((imgObject) => {
      // Backend expects 'images' field name
      formDataApi.append('images', imgObject.file, imgObject.file.name);
    });
  }

  // Append User's existing listing images
  const existingImages = (uploadedImages || []).filter(img => {
    return img.backgroundOption === 'keep';
  });
  if (existingImages.length > 0) {
    existingImages.forEach((imgObject) => {
      formDataApi.append('existing_images', imgObject.file, imgObject.file.name);
    });
  }

  if (singleImage && singleImage.file) {
    formDataApi.append('single_image', singleImage.file, singleImage.file.name);
  } else {
  }

  // Append A+ template images (multiple)
  if (aPlusSequence && aPlusSequence.length > 0) {
    aPlusSequence.forEach((imgObject) => {
      if (imgObject.file) {
        formDataApi.append('aplus_template_images', imgObject.file, imgObject.file.name);
      }
    });
  }

  // Append B+ template images (up to 6)
  if (bPlusSequence && bPlusSequence.length > 0) {
    const bPlusToSend = bPlusSequence.slice(0, 6);
    
    bPlusToSend.forEach((imgObject) => {
      if (imgObject && imgObject.file) {
        formDataApi.append('bplus_template_images', imgObject.file, imgObject.file.name);
      }
    });
  }

  selectedTemplatesWithImages.forEach((template) => {
    if (template.images) {
      template.images.forEach((imgObject) => {
        if (imgObject && imgObject.file) {
          formDataApi.append(`template_${template.id}`, imgObject.file, imgObject.file.name);
        }
      });
    }
  });

  // Handle dimension image
  if (dimensionData && dimensionData.dimensionImage && dimensionData.dimensionImage.file) {
    formDataApi.append('dimension_image', dimensionData.dimensionImage.file, dimensionData.dimensionImage.file.name);
  }

return {
  endpoint: `${API_BASE_URL}/single-gen/generate`,
  formDataApi
};


};

/**
 * Validates the form data before submission
 * @param {Object} formData - The form data
 * @param {Array} productImages - Array of product images
 * @param {Array} selectedTemplates - Array of selected template indices
 * @param {String} countError - Current count error message
 * @param {Object} selectedAPlusTemplate - Selected A+ template
 * @param {Array} aPlusSequence - Array of A+ template images
 * @returns {Object} Validation result with error message if any
 */
export const validateFormData = (formData, productImages, selectedTemplates, countError, selectedAPlusTemplate = null, aPlusSequence = []) => {
  // Check for template/image count mismatch error
  if (countError) {
    return {
      isValid: false,
      errorMessage: `Please resolve the image/template issue: ${countError}`
    };
  }

  // Validate required fields
  if (!formData.brandName?.trim()) {
    return {
      isValid: false,
      errorMessage: "Brand Name is required."
    };
  }

  if (!formData.briefDescription?.trim()) {
    return {
      isValid: false,
      errorMessage: "Brief product description is required."
    };
  }

  // Validate A+ template selection
  if (selectedAPlusTemplate && (!aPlusSequence || aPlusSequence.length === 0)) {
    return {
      isValid: false,
      errorMessage: "Please upload at least one image for the selected A+ template."
    };
  }

  // Re-check conditions explicitly before submitting
  if (productImages.length > 0 && selectedTemplates.length > 0 && productImages.length !== selectedTemplates.length) {
    return {
      isValid: false,
      errorMessage: "Image count and template count must match when both are selected."
    };
  }

  if (productImages.length === 0 && selectedTemplates.length > 0) {
    return {
      isValid: false,
      errorMessage: "Please upload images to use the selected templates, or deselect the templates."
    };
  }

  return { isValid: true };
};

/**
 * Handle API response error
 * @param {Response} response - Fetch API response
 * @returns {Promise<string>} Error message
 */
export const handleApiError = async (response) => {
  let errorText = `Request failed with status: ${response.status}`;
  let errorDetails = null;
  
  try {
    const errorData = await response.json();
    errorDetails = errorData?.error || errorData?.message || errorData?.detail || JSON.stringify(errorData);
    errorText = `${errorText}: ${errorDetails}`;
  } catch (jsonError) {
    // If response is not JSON
    try {
      const textResponse = await response.text();
      if (textResponse) errorText = `${errorText}: ${textResponse}`;
    } catch (textError) { /* Ignore further errors */ }
  }
  
  return errorText;
};

/**
 * Maps the data received from the Rainforest API to our form fields
 * @param {Object} rainforestData - The data received from the Rainforest API
 * @returns {Object} Mapped data including formData, existingInput, and whether to switch mode
 */
export const mapRainforestDataToFormFields = (rainforestData) => {
  if (!rainforestData) {
    return null;
  }

  const result = {
    formData: {
      brandName: rainforestData.brand_name || rainforestData.brandName || '',
      category: rainforestData.category || rainforestData.Category || '',
      subCategory: rainforestData.sub_category || rainforestData.subCategory || '',
      productType: rainforestData.product_type || rainforestData.productType || '',
      briefDescription: rainforestData.briefDescription || rainforestData.description || rainforestData.product_description || '',
      // Don't auto-fill keywords - leave empty for user to fill manually
      keywords: '',
      // Don't auto-fill bannedKeywords - leave empty for user to fill manually
      bannedKeywords: '',
      modelNumber: rainforestData.model_number || rainforestData.modelNumber || '',
      skuId: rainforestData.sku_id || rainforestData.skuId || '',
      color: rainforestData.color || rainforestData.Color || '',
      // Only auto-fill material if it was actually found and not empty
      // Don't fill if it's an empty string, null, or undefined
      material: (rainforestData.material && typeof rainforestData.material === 'string' && rainforestData.material.trim() !== '') 
        ? rainforestData.material.trim()
        : ((rainforestData.Material && typeof rainforestData.Material === 'string' && rainforestData.Material.trim() !== '') 
          ? rainforestData.Material.trim() 
          : ''),
      // Only auto-fill size if it was actually found and not empty
      // Don't fill if it's an empty string, null, or undefined
      size: (rainforestData.size && typeof rainforestData.size === 'string' && rainforestData.size.trim() !== '') 
        ? rainforestData.size.trim()
        : ((rainforestData.Size && typeof rainforestData.Size === 'string' && rainforestData.Size.trim() !== '') 
          ? rainforestData.Size.trim() 
          : ''),
      eanNumber: rainforestData.ean_number || rainforestData.eanNumber || '',
      feature1: rainforestData.feature1 || (rainforestData['Feature 1'] || rainforestData['Key Attribute 1'] || '') ||
        (Array.isArray(rainforestData.key_attributes) && rainforestData.key_attributes[0] || ''),
      feature2: rainforestData.feature2 || (rainforestData['Feature 2'] || rainforestData['Key Attribute 2'] || '') ||
        (Array.isArray(rainforestData.key_attributes) && rainforestData.key_attributes[1] || ''),
      feature3: rainforestData.feature3 || (rainforestData['Feature 3'] || rainforestData['Key Attribute 3'] || '') ||
        (Array.isArray(rainforestData.key_attributes) && rainforestData.key_attributes[2] || ''),
      feature4: rainforestData.feature4 || (rainforestData['Feature 4'] || rainforestData['Key Attribute 4'] || '') ||
        (Array.isArray(rainforestData.key_attributes) && rainforestData.key_attributes[3] || ''),
      feature5: rainforestData.feature5 || (rainforestData['Feature 5'] || rainforestData['Key Attribute 5'] || '') ||
        (Array.isArray(rainforestData.key_attributes) && rainforestData.key_attributes[4] || ''),
      application1: rainforestData.application1 || '',
      application2: rainforestData.application2 || '',
    },
    existingInput: {
      title: rainforestData.existing_title || rainforestData.title || rainforestData.Title || '',
      bullets: [],
      description: rainforestData.existing_description || rainforestData.description || '',
    }
  };

  // Handle bullet points - they could be in different formats
  const bullets = rainforestData.bullets || rainforestData.existing_bullets || [];
  
  // Prepare an array with bullets (max 10, min 5)
  result.existingInput.bullets = [];
  
  // Fill in available bullets (limited to 10)
  if (Array.isArray(bullets) && bullets.length > 0) {
    result.existingInput.bullets = bullets.slice(0, 10).map(bullet => bullet || ''); // MAX_BULLETS = 10
  }
  
  // Ensure we always have at least 5 bullet fields (fill with empty strings if needed)
  while (result.existingInput.bullets.length < 5) {
    result.existingInput.bullets.push('');
  }
  
  // Alternative bullet point formats - check for additional bullets beyond the main array (max 10 total)
  for (let i = 0; i < MAX_BULLETS; i++) {
    const bulletKey = `bullet-${i}`;
    const bulletPointKey = `Bullet Point ${i + 1}`;
    
    // If we have a bullet at this index and it's empty, try to fill it
    if (i < result.existingInput.bullets.length && result.existingInput.bullets[i] === '' && (rainforestData[bulletKey] || rainforestData[bulletPointKey])) {
      result.existingInput.bullets[i] = rainforestData[bulletKey] || rainforestData[bulletPointKey];
    }
    // If we don't have a bullet at this index but there's data, add it (but don't exceed max)
    else if (i >= result.existingInput.bullets.length && result.existingInput.bullets.length < MAX_BULLETS && (rainforestData[bulletKey] || rainforestData[bulletPointKey])) {
      result.existingInput.bullets.push(rainforestData[bulletKey] || rainforestData[bulletPointKey]);
    }
  }

  // Check if we should switch to existing mode
  const hasExistingTitle = !!rainforestData.existing_title || !!rainforestData.title || !!rainforestData.Title;
  const hasExistingBullets = Array.isArray(bullets) && bullets.some(b => b?.trim());
  const hasExistingDescription = !!rainforestData.existing_description || !!rainforestData.description;
  
  result.shouldSwitchToExistingMode = hasExistingTitle && (hasExistingBullets || hasExistingDescription);

  return result;
};